import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Send,
  Sparkles,
  User,
  Mail,
  X,
  Shield,
  Clock,
  MessageSquare,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { TrustBadge } from "../components/EmptyState";

const Coach = ({ user }) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: "assistant",
      content: "Hi! I'm your Stelx Pay financial coach. How can I help you today?",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [showExitModal, setShowExitModal] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [email, setEmail] = useState(user?.email || "");

  // Fetch recent sessions (within 24 hours)
  const { data: sessionsData, refetch: refetchSessions } = useQuery({
    queryKey: ["aiSessions"],
    queryFn: async () => {
      const res = await axios.get(`${API}/ai/sessions`);
      return res.data;
    }
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Chat mutation using new /ai/chat endpoint
  const chatMutation = useMutation({
    mutationFn: async (message) => {
      const response = await axios.post(`${API}/ai/chat`, { 
        message,
        session_id: sessionId
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Update session ID
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: "assistant",
        content: data.response,
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      }]);
    },
    onError: () => {
      toast.error("Unable to get response. Please try again.");
    }
  });

  // Email transcript mutation
  const emailTranscriptMutation = useMutation({
    mutationFn: async (sid) => {
      const res = await axios.post(`${API}/ai/sessions/${sid}/email-transcript`);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.status === "success") {
        toast.success(`Transcript sent to ${user?.email}`);
      } else if (data.status === "mock") {
        toast.info("Email service not configured. Transcript logged.");
      } else {
        toast.error("Failed to send transcript");
      }
    },
    onError: () => {
      toast.error("Failed to send transcript");
    }
  });

  // Load session
  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/ai/sessions/${sid}`);
      setSessionId(sid);
      setMessages(res.data.messages.map((m, i) => ({
        id: i,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp
      })));
      setShowSessions(false);
    } catch (err) {
      toast.error("Session expired or not found");
    }
  };

  const handleSend = () => {
    if (!inputMessage.trim() || chatMutation.isPending) return;
    
    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: "user",
      content: inputMessage.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }]);
    
    const message = inputMessage.trim();
    setInputMessage("");
    chatMutation.mutate(message);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = () => {
    if (messages.length > 1 && sessionId) {
      setShowExitModal(true);
    } else {
      navigate("/explore");
    }
  };

  const handleExitWithTranscript = () => {
    if (sessionId) {
      emailTranscriptMutation.mutate(sessionId);
    }
    setShowExitModal(false);
    navigate("/explore");
  };

  const handleExitWithoutTranscript = () => {
    setShowExitModal(false);
    navigate("/explore");
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([{
      id: 1,
      role: "assistant",
      content: "Hi! I'm your Stelx Pay financial coach. How can I help you today?",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }]);
    setShowSessions(false);
  };

  const sessions = sessionsData?.sessions || [];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="coach-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-base">Your Coach</h1>
              <p className="text-[10px] text-muted-foreground">
                {sessionId ? "Session active" : "New conversation"}
              </p>
            </div>
          </div>
        </div>
        
        {/* Sessions button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSessions(true)}
          className="text-xs text-muted-foreground"
          data-testid="sessions-btn"
        >
          <Clock className="w-4 h-4 mr-1" />
          History
        </Button>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Trust message at top */}
        {messages.length === 1 && (
          <div className="text-center py-2 mb-2">
            <TrustBadge
              icon={Shield}
              text="Sessions saved for 24hrs. Request transcript via email anytime."
              variant="security"
            />
          </div>
        )}
        
        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[85%] flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
              {/* Avatar */}
              <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                msg.role === "user" ? "bg-primary/20" : "bg-white/10"
              }`}>
                {msg.role === "user" ? (
                  <User className="w-3.5 h-3.5 text-primary" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </div>
              
              {/* Message bubble */}
              <div className={`rounded-2xl px-4 py-3 ${
                msg.role === "user" 
                  ? "bg-primary text-primary-foreground rounded-tr-sm" 
                  : "glass rounded-tl-sm"
              }`}>
                <p className="text-sm leading-relaxed">{msg.content}</p>
                <p className={`text-[10px] mt-1 ${
                  msg.role === "user" ? "text-primary-foreground/60" : "text-muted-foreground/50"
                }`}>
                  {msg.timestamp}
                </p>
              </div>
            </div>
          </div>
        ))}
        
        {/* Typing indicator */}
        {chatMutation.isPending && (
          <div className="flex justify-start">
            <div className="max-w-[85%] flex gap-2">
              <div className="w-7 h-7 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div className="glass rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </main>

      {/* Input */}
      <div className="px-5 py-4 border-t border-white/5">
        <div className="flex gap-3">
          <Input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            className="flex-1 bg-muted/30 border-white/10"
            disabled={chatMutation.isPending}
            data-testid="chat-input"
          />
          <Button 
            onClick={handleSend}
            disabled={!inputMessage.trim() || chatMutation.isPending}
            className="btn-primary px-4"
            data-testid="send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Exit Modal */}
      {showExitModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="glass w-full max-w-sm rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">End Session?</h3>
              <button onClick={() => setShowExitModal(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Your conversation is saved for 24 hours. Would you like a copy emailed to you?
            </p>
            
            <div className="flex items-center gap-2 p-3 bg-white/5 rounded-lg mb-4">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{user?.email || "No email on file"}</span>
            </div>
            
            <div className="space-y-2">
              <Button 
                onClick={handleExitWithTranscript}
                className="w-full btn-primary"
                disabled={emailTranscriptMutation.isPending}
              >
                <Mail className="w-4 h-4 mr-2" />
                {emailTranscriptMutation.isPending ? "Sending..." : "Email Transcript & Exit"}
              </Button>
              <Button 
                onClick={handleExitWithoutTranscript}
                variant="outline"
                className="w-full"
              >
                Exit Without Email
              </Button>
            </div>
            
            <p className="text-[10px] text-center text-muted-foreground mt-3">
              Sessions retained for regulatory compliance
            </p>
          </div>
        </div>
      )}

      {/* Sessions Drawer */}
      {showSessions && (
        <div className="fixed inset-0 z-50 bg-black/80" onClick={() => setShowSessions(false)}>
          <div 
            className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-background border-l border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-semibold">Recent Sessions</h3>
              <button onClick={() => setShowSessions(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            
            <div className="p-4">
              <Button 
                onClick={startNewSession}
                className="w-full btn-primary mb-4"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                New Conversation
              </Button>
              
              <p className="text-xs text-muted-foreground mb-3">
                Sessions visible for {sessionsData?.visibility_hours || 24} hours
              </p>
              
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No recent sessions
                </p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      className="w-full p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium truncate flex-1">{session.preview || "Conversation"}</p>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(session.started_at).toLocaleString()} • {session.message_count} messages
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coach;
